import { Injectable, inject } from '@angular/core';
import { ActivatedRouteSnapshot, Route, Router, RouterStateSnapshot, UrlSegment, UrlTree } from '@angular/router';
import { RoutePath } from '@nano/common/routing/routing';
import { CONFIG } from '@nano/core/config';
import { AuthService } from '@nano/core/auth';
import { filter, isObservable, map, Observable } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class FeatureEnabledGuard {
  private config = inject(CONFIG);

  constructor(
    private router: Router,
    private authService: AuthService
  ) {}

  canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): boolean | UrlTree | Observable<boolean | UrlTree> {
    return this.toActivationResult(this.isFeatureEnabled(state.url));
  }

  canLoad(route: Route): boolean | Observable<boolean> {
    return this.isFeatureEnabled(route.path);
  }

  canMatch(route: Route, segments: UrlSegment[]): boolean | Observable<boolean> {
    return this.isFeatureEnabled(route.path ?? segments.map((segment) => segment.path).join('/'));
  }

  private toActivationResult(isEnabled: boolean | Observable<boolean>): boolean | UrlTree | Observable<boolean | UrlTree> {
    if (isObservable(isEnabled)) {
      return isEnabled.pipe(map((enabled) => enabled || this.router.createUrlTree([RoutePath.error])));
    }

    return isEnabled || this.router.createUrlTree([RoutePath.error]);
  }

  isFeatureEnabled(path: string): boolean | Observable<boolean> {
    const normalizedPath = this.normalizePath(path);

    if (normalizedPath === `${RoutePath.settings}/${RoutePath.accounts}`) {
      return this.config.userRightsSettingsEnabled || this.config.renameAccountSettingsEnabled;
    }

    switch (normalizedPath.split('/').pop() as RoutePath) {
      case RoutePath.signup:
        return !this.config.oauthEnabled && this.config.registrationEnabled;
      case RoutePath.cards:
        return this.getUserFeatureAccess(
          (profile) =>
            ((profile.privatePerson && this.config.cardsPrivateEnabled) || (!profile.privatePerson && this.config.cardsCorporateEnabled)) &&
            !!profile.canAccessCards
        );
      case RoutePath.messages:
        return this.config.messagesEnabled;
      case RoutePath.paymentImport:
        return this.getUserFeatureAccess(
          (profile) =>
            (profile.privatePerson && this.config.paymentImportPrivateEnabled) || (!profile.privatePerson && this.config.paymentImportCorporateEnabled)
        );
      case RoutePath.referral:
        return this.config.referralEnabled;
      case RoutePath.scheduledPayments:
        return this.config.scheduledPaymentsEnabled;
      case RoutePath.pendingPayments:
        return this.getUserFeatureAccess(
          (profile) => (this.config.paymentSavePrivateEnabled && profile.privatePerson) || (this.config.paymentSaveCorporateEnabled && !profile.privatePerson)
        );
      case RoutePath.recipients:
        return this.config.paymentBeneficiaryManagementEnabled;
      case RoutePath.passwordRecovery:
        return this.config.emailAsUserId;
      case RoutePath.agreements:
        return this.config.displayAgreements;
      case RoutePath.userRights:
        return this.config.userRightsSettingsEnabled;
      case RoutePath.enduserApi:
        return this.config.enduserApiPrivateEnabled || this.config.enduserApiCorporateEnabled;
      case RoutePath.renameAccount:
        return this.config.renameAccountSettingsEnabled;
      case RoutePath.changePassword:
        return this.config.allowChangePassword;
      case RoutePath.loans:
        return this.config.ebanksisLoansEnabled;
      default:
        return true;
    }
  }

  private getUserFeatureAccess(predicate: (profile: NonNullable<AuthService['userProfile']>) => boolean): boolean | Observable<boolean> {
    if (!this.authService.isAuthorized()) {
      return true;
    }

    if (this.authService.userProfile) {
      return predicate(this.authService.userProfile);
    }

    return this.authService.userProfile$.pipe(
      filter((profile): profile is NonNullable<AuthService['userProfile']> => !!profile),
      map(predicate)
    );
  }

  private normalizePath(path = ''): string {
    return path.replace(/^\/+/, '').split('?')[0];
  }
}
